import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';

import { StorageService } from '../_services/storage.service';
import { SettingsService } from '../_services/settings.service';
import { AuthService } from '../_services/auth.service';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { RefreshTokenRequestDto, TokenDto } from '../_services/auth.dto';

const USER_KEY = 'auth-user';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  // Plusieurs requêtes peuvent échouer en même temps : une seule sortie vers /login
  private endingSession = false;

  constructor(
    private storageService: StorageService,
    private authService: AuthService,
    private settingsService:SettingsService
  ) {}

  getToken(){
    const user = window.localStorage.getItem(USER_KEY);
    if (user) {
      return JSON.parse(user)["access_token"];
    }
  }

  getClientId(){
    const clientSession = window.sessionStorage.getItem('client-security') 
    let clientStorage=""
    if (clientSession){
      const user = window.localStorage.getItem(USER_KEY);
      if (user) {
        clientStorage = JSON.parse(user)["client_security"];
      }
  
      if (clientSession == clientStorage){
        return clientStorage
      }
    }

    return ""

   }

  getRefreshToken(){
    const user = window.localStorage.getItem(USER_KEY);
    if (user) {
      return JSON.parse(user)["refresh_token"];
    } else {
      return null
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

private isBinaryUpload(req: HttpRequest<any>): boolean {
  if (req.body instanceof Blob) return true;

  // Repli sur le Content-Type quand l'appelant le pose lui-même.
  const ct = req.headers.get('Content-Type') || '';
  if (ct.startsWith('application/octet-stream')) return true;

  return false;
}


  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldBypass(req)) {
      return next.handle(req); // ne clone pas, ne touche pas aux headers, pas de 401 handling
    }

      if (this.isBinaryUpload(req)) {
    // Laisse passer tel quel : pas de clone, pas de withCredentials, pas de token
    return next.handle(req);
  }


    req = req.clone({
      withCredentials: true,
      setHeaders: {Authorization: `Bearer ${this.getToken()}`}
    });
    return next.handle(req).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          !req.url.includes('auth/login') &&
          !req.url.includes('auth/sso') &&
          !req.url.includes('auth/lostpassword') &&
          !req.url.includes('auth/first-password') &&
          !req.url.includes('healthchecker') &&
          error.status === 401
        ) {
          return this.handle401Error(req, next, error);
        }

        return throwError(() => error);
      })
    );
  }

  private BYPASS_HOSTS = new Set<string>([
    'api-search.synth-ia.ovh'
  ]);  

  private shouldBypass(req: HttpRequest<any>): boolean {
    try {
      // Si URL absolue
      const url = new URL(req.url);
      // compare uniquement le host (ex: api-search.synth-ia.ovh)
      if (this.BYPASS_HOSTS.has(url.host)) {
        return true;
      }
    } catch {
      // URL relative -> ne pas bypass
    }
    return false;
  }


  private handle401Error(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // Un rafraîchissement est déjà en cours : on attend son issue plutôt que d'en lancer un second
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => this.replay(request, next, token))
      );
    }

    const refreshToken = this.getRefreshToken();
    if (!this.storageService.isLoggedIn() || refreshToken == null) {
      this.endSession();
      return throwError(() => error);
    }

    this.isRefreshing = true;
    // Sujet neuf à chaque tentative : un échec précédent l'a clos en erreur
    this.refreshTokenSubject = new BehaviorSubject<string | null>(null);

    return this.authService.refreshToken(refreshToken, this.getClientId()).pipe(
      catchError((refreshError) => {
        this.isRefreshing = false;
        // Les requêtes en attente échouent avec le refresh au lieu de rester suspendues
        this.refreshTokenSubject.error(refreshError);

        // Tout refus du refresh (400 invalid_grant, 401, 403, 488…) signe un refresh token mort,
        // typiquement hérité d'une version précédente : rester connecté ferait échouer chaque appel.
        // Les 5xx et coupures réseau restent passagers : on garde la session pour un nouvel essai.
        if (refreshError.status >= 400 && refreshError.status < 500) {
          this.endSession();
        }
        return throwError(() => refreshError);
      }),
      switchMap((token: TokenDto) => {
        this.storageService.saveAfterRefresh(token);
        this.isRefreshing = false;
        this.refreshTokenSubject.next(token.access_token);
        this.settingsService.loadSettings().subscribe();
        return this.replay(request, next, token.access_token);
      })
    );
  }

  /** Rejoue la requête avec le token neuf ; s'il est refusé à son tour, la session est perdue. */
  private replay(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    return next.handle(this.addToken(request, token)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.endSession();
        }
        return throwError(() => error);
      })
    );
  }

  /** Session inutilisable : on repart de la page de connexion (qui renvoie vers le SSO s'il est actif). */
  private endSession(): void {
    // Une déconnexion est déjà en route (vers Keycloak le cas échéant) : ne pas la détourner
    if (this.endingSession || this.storageService.isSigningOut()) {
      return;
    }
    this.endingSession = true;
    this.storageService.clean();
    this.goToLogin();
  }

  /**
   * Chargement complet de /login, et non navigation Angular : il vide la mémoire des services
   * (listes, flux…) pour que la session suivante n'hérite de rien, en un seul démarrage de l'app.
   */
  protected goToLogin(): void {
    window.location.assign('/login');
  }
}

export const httpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
];
